import { useTranslation } from "@/hooks";
import { logger } from "@/utils/debug";
import { ActionIcon, Input, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import "./AiSearch.css";

export function AiSearch({
  onPrompt,
  onFocus,
  onBlur,
  placeholder,
  width,
  defaultPrompt,
  autoFocus = true,
  loading = false,
  viewTransitionName,
}: {
  onPrompt: (prompt: string) => Promise<void>;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  width?: number | string;
  collapsed?: boolean;
  defaultPrompt?: string | null;
  autoFocus?: boolean;
  loading?: boolean;
  viewTransitionName?: string;
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    initialValues: {
      prompt: defaultPrompt || "",
    },
  });

  async function handleSubmit(values: { prompt: string }) {
    if (values.prompt.trim().length === 0) return;
    setIsLoading(true);
    try {
      await onPrompt(values.prompt);
    } catch (err) {
      logger.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  const isFormLoading = isLoading || loading;

  const formStyle: React.CSSProperties = {
    maxWidth: width,
  };

  if (viewTransitionName) {
    formStyle.viewTransitionName = viewTransitionName;
  }

  const inputStyles = {
    input: {
      color: isFormLoading
        ? "var(--mantine-color-gray-5)"
        : "var(--mantine-color-dark-9)",
    },
  };

  return (
    <form
      onSubmit={form.onSubmit(handleSubmit)}
      className="ai-search"
      style={formStyle}
    >
      <Tooltip openDelay={500} label={t("aiChat").uploadFile} withArrow offset={10}>
        <ActionIcon c="gray.9" variant="subtle" bdrs="xl" size={44}>
          <IconPlus
            size={22}
            stroke={1.6}
            color="var(--mantine-color-gray-9)"
          />
        </ActionIcon>
      </Tooltip>
      <Input
        readOnly={isFormLoading}
        placeholder={placeholder}
        variant="unstyled"
        className="ai-search-input"
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={onBlur}
        flex={1}
        h={36}
        styles={inputStyles}
        {...form.getInputProps("prompt")}
      />
      <ActionIcon
        loading={isFormLoading}
        c="eu_flag.9"
        bdrs="xl"
        size={44}
        type="submit"
      >
        <IconSearch size={18} stroke={2} color="white" />
      </ActionIcon>
    </form>
  );
}
